import { itemTemplateApi } from "#/api/itemTemplateApi";
import { productApi } from "#/api/productApi";
import { sitesApi } from "#/api/sitesApi";
import ActionButton from "#/components/buttons/ActionButton";
import DetailPageLayout from "#/components/ui/DetailPageLayout";
import {
  MaintenanceStatus,
  RetrievalStatus,
} from "#/types/items";
import type { CreateItemTemplatePayload } from "#/types/itemTemplate";
import { sanitize, sanitizeItemStatusLabel } from "#/utils/StringUtils";
import {
  Accordion,
  Button,
  Select,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  showErrorNotification,
  showSuccessNotification,
} from "#/components/notifications";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { useAllowedStatuses } from "#/hooks/useAllowedStatuses";

const TemplateDetailPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { canWrite } = usePermission();
  const allowedStatuses = useAllowedStatuses();

  const emptyForm = {
    name: "",
    description: "",
    product_id: undefined,
    site_id: null,
    notify_email: "",
    location: "",
    customize_columns: {},
    status: undefined,
    maintenance_status: undefined,
    retrieval_status: undefined,
  };

  const form = useForm<CreateItemTemplatePayload>({
    initialValues: emptyForm,
    validate: {
      name: (value) => (value ? null : t("templates.validation.templateNameRequired")),
    },
  });

  const { data: { products = [] } = {} } = useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const response = await productApi.getAll({
        page: 1,
        limit: 10000,
      });
      if (!response.data) {
        throw new Error("Failed to fetch categories");
      }
      return {
        products: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  const { data: { sites = [] } = {} } = useQuery({
    queryKey: ["sites"],
    queryFn: async () => {
      const response = await sitesApi.getAll({ page: 1, limit: 10000 });
      if (!response.data) {
        throw new Error("Failed to fetch sites");
      }
      return {
        sites: response.data.data,
      };
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  const { data: { statuses = [] } = {} } = useQuery({
    queryKey: ["product-statuses", form.values.product_id],
    queryFn: async () => {
      if (!form.values.product_id) {
        return {
          statuses: [],
        };
      }
      const response = await productApi.getStatuses(
        Number(form.values.product_id)
      );
      if (!response.data) {
        throw new Error("Failed to fetch statuses");
      }
      return {
        statuses: response.data,
      };
    },
    enabled: form.values.product_id !== undefined,
    staleTime: 1000 * 60,
  });

  const templateQuery = useQuery({
    queryKey: ["template-details", id],
    queryFn: async () => {
      if (!id || id === "new") {
        return;
      }
      const response = await itemTemplateApi.getById(id);
      if (!response.data) {
        throw new Error("Failed to fetch template");
      }

      const template = response.data;
      form.setValues({
        name: template.name,
        description: template.description ?? "",
        site_id: template?.site?.id.toString() ?? null,
        product_id: template?.product?.id.toString() ?? undefined,
        notify_email: template.notify_email ?? "",
        location: template.location ?? "",
        customize_columns: template.customize_columns ?? {},
        status: template.status ?? undefined,
        maintenance_status: template.maintenance_status ?? undefined,
        retrieval_status: template.retrieval_status ?? undefined,
      });
      form.resetDirty();

      return response.data;
    },
    enabled: id !== undefined && id !== "new",
  });

  const handleSubmit = (values: typeof form.values) => {
    const payload: CreateItemTemplatePayload = {
      name: values.name,
      description: values.description || undefined,
      site_id: values.site_id ? Number(values.site_id) : null,
      product_id: values.product_id ? Number(values.product_id) : null,
      location: values.location || undefined,
      notify_email: values.notify_email || undefined,
      status: values.status || undefined,
      retrieval_status: values.retrieval_status || undefined,
      maintenance_status: values.maintenance_status || undefined,
      customize_columns: Object.keys(values.customize_columns ?? {}).length > 0
        ? values.customize_columns
        : undefined,
    };

    templateMutation.mutate(payload);
  };

  const templateMutation = useMutation({
    mutationFn: async (payload: CreateItemTemplatePayload) => {
      if (id !== "new") {
        return await itemTemplateApi.update(Number(id), payload);
      } else {
        return await itemTemplateApi.create(payload);
      }
    },
    onSuccess: (data) => {
      if (id === "new") {
        void navigate(`/inventory/templates/${data.data.id}`);
      }
      showSuccessNotification({
        message: id === "new" ? t("templates.createdSuccess") : t("templates.updatedSuccess"),
      });
    },
    onError: (error) => {
      showErrorNotification({
        message: error.message ?? t("common.failedToProcess"),
      });
    },
  });

  return (
    <DetailPageLayout
      title={id != "new" ? t("templates.templateDetails") : t("templates.createTemplate")}
      loading={templateMutation.isPending || templateQuery.isLoading}
      navRightSection={
        <div className={`w-full flex gap-2 items-center`}>
          <Button
            color="black"
            variant="outline"
            type="reset"
            disabled={!form.isDirty()}
            onClick={() => {
              form.reset();
            }}
            size="xs"
          >
            {t("common.clear")}
          </Button>

          <PermissionTooltip disabled={!canWrite()}>
            <Button
              color="black"
              variant="filled"
              loading={templateMutation.isPending}
              disabled={!form.isDirty() || !canWrite()}
              onClick={() => {
                form.onSubmit(handleSubmit)();
              }}
              size="xs"
            >
              {t("common.save")}
            </Button>
          </PermissionTooltip>
        </div>
      }
    >
      <div className="w-full flex flex-col">
        <form
          className="space-y-8 relative"
          onSubmit={form.onSubmit(handleSubmit)}
        >
          <Accordion
            variant={"separated"}
            defaultValue={["template_info", "item_defaults", "customize_columns"]}
            multiple
          >
            <Accordion.Item value={"template_info"}>
              <Accordion.Control>{t("templates.templateDetails")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full grid grid-cols-1 gap-4">
                  <TextInput
                    label={t("templates.form.templateName")}
                    withAsterisk
                    disabled={!canWrite() || templateQuery.isLoading || templateMutation.isPending}
                    placeholder={t("templates.form.templateNamePlaceholder")}
                    {...form.getInputProps("name")}
                  />

                  <Textarea
                    label={t("templates.form.description")}
                    disabled={!canWrite() || templateQuery.isLoading || templateMutation.isPending}
                    placeholder={t("templates.form.descriptionPlaceholder")}
                    rows={3}
                    {...form.getInputProps("description")}
                  />
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value={"item_defaults"}>
              <Accordion.Control>{t("templates.itemDefaults")}</Accordion.Control>
              <Accordion.Panel>
                <div className="w-full grid grid-cols-1 gap-4">
                  <div className="w-full grid grid-cols-2 gap-4">
                    <Select
                      label={t("templates.form.site")}
                      placeholder={t("templates.form.sitePlaceholder")}
                      searchable
                      disabled={
                        !canWrite() || templateQuery.isLoading || templateMutation.isPending
                      }
                      data={sites.map((site) => {
                        return {
                          label: site?.name ?? "",
                          value: site?.id?.toString() ?? "",
                        };
                      })}
                      clearable
                      {...form.getInputProps("site_id")}
                    />

                    <Select
                      label={t("templates.form.category")}
                      placeholder={t("templates.form.categoryPlaceholder")}
                      searchable
                      disabled={
                        !canWrite() || templateQuery.isLoading || templateMutation.isPending
                      }
                      data={products.map((prod) => {
                        return {
                          label: prod?.name ?? "",
                          value: prod?.id?.toString() ?? "",
                        };
                      })}
                      clearable
                      {...form.getInputProps("product_id")}
                    />
                  </div>

                  <div className="w-full grid grid-cols-3 gap-4">
                    <Select
                      label={t("templates.form.status")}
                      placeholder={t("templates.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={[
                        {
                          group: t("common.default"),
                          items: allowedStatuses.map((status) => ({
                            label: sanitizeItemStatusLabel(status),
                            value: status,
                          })),
                        },
                        {
                          group: t("common.categoryStatuses"),
                          items: (() => {
                            const allowedValueSet = new Set<string>(allowedStatuses);
                            const seenLabels = new Set<string>();
                            return statuses.filter((scanStatus) => {
                              const label = scanStatus.label ?? "";
                              if (!label || allowedValueSet.has(label)) {
                                return false;
                              }
                              if (seenLabels.has(label)) {
                                return false;
                              }
                              seenLabels.add(label);
                              return true;
                            }).map((scanStatus) => ({
                              label: `Step ${scanStatus.step} : ${scanStatus.label}`,
                              value: scanStatus.label ?? "",
                            }));
                          })(),
                        },
                      ]}
                      value={form.values.status}
                      clearable
                      onChange={(value) => {
                        form.setFieldValue("status", value);
                      }}
                    />

                    <Select
                      label={t("templates.form.retrievalStatus")}
                      placeholder={t("templates.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={Object.values(RetrievalStatus).map((status) => ({
                        label: sanitize(status),
                        value: status,
                      }))}
                      value={form.values.retrieval_status}
                      clearable
                      onChange={(value) => {
                        form.setFieldValue("retrieval_status", value);
                      }}
                    />

                    <Select
                      label={t("templates.form.maintenanceStatus")}
                      placeholder={t("templates.form.statusPlaceholder")}
                      searchable
                      disabled={!canWrite()}
                      data={Object.values(MaintenanceStatus).map((status) => ({
                        label: sanitize(status),
                        value: status,
                      }))}
                      value={form.values.maintenance_status}
                      clearable
                      onChange={(value) => {
                        form.setFieldValue("maintenance_status", value);
                      }}
                    />
                  </div>

                  <div className="w-full grid grid-cols-2 gap-4">
                    <TextInput
                      label={t("templates.form.location")}
                      disabled={!canWrite() || templateQuery.isLoading || templateMutation.isPending}
                      placeholder={t("templates.form.locationPlaceholder")}
                      {...form.getInputProps("location")}
                    />

                    <TextInput
                      label={t("templates.form.manufacturerEmail")}
                      disabled={!canWrite() || templateQuery.isLoading || templateMutation.isPending}
                      placeholder={t("templates.form.manufacturerEmailPlaceholder")}
                      type="email"
                      {...form.getInputProps("notify_email")}
                    />
                  </div>
                </div>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="customize_columns">
              <Accordion.Control>
                <div className="flex items-center gap-2">
                  <span>{t("common.customFields")}</span>
                  <PermissionTooltip disabled={!canWrite()}>
                    <ActionButton
                      color="black"
                      size="xs"
                      icon={IconPlus}
                      onClick={(e) => {
                        e.stopPropagation();
                        const existing = { ...form.values.customize_columns };
                        let newKey = `field_${Object.keys(existing).length + 1}`;
                        while (newKey in existing) {
                          newKey += "_";
                        }
                        form.setFieldValue("customize_columns", {
                          ...existing,
                          [newKey]: "",
                        });
                      }}
                      disabled={!canWrite()}
                    />
                  </PermissionTooltip>
                </div>
              </Accordion.Control>

              <Accordion.Panel>
                {Object.entries(form.values.customize_columns ?? {}).length <=
                0 ? (
                  <div>{t("common.addCustomFieldHint")}</div>
                ) : (
                  <div className="w-full flex flex-col gap-2">
                    {Object.entries(form.values.customize_columns ?? {}).map(
                      ([key, value], index) => {
                        const fieldId = `${key}_${index}`;

                        return (
                          <div
                            key={fieldId}
                            className="w-full grid grid-cols-[auto_1fr_1fr_auto] gap-2 items-center"
                          >
                            <span className="bg-black/5 font-bold text-black/70 rounded-full p-2 w-8 h-8 flex items-center justify-center">
                              <span>{index + 1}</span>
                            </span>
                            <TextInput
                              defaultValue={key}
                              disabled={!canWrite()}
                              onBlur={(event) => {
                                const newKey = event.currentTarget.value.trim();
                                if (!newKey || newKey === key) return;

                                const updated = {
                                  ...form.values.customize_columns,
                                };
                                const val = updated[key];
                                delete updated[key];

                                if (newKey in updated) return;

                                updated[newKey] = val;
                                form.setFieldValue(
                                  "customize_columns",
                                  updated
                                );
                              }}
                            />

                            <TextInput
                              value={value}
                              disabled={!canWrite()}
                              onChange={(event) => {
                                form.setFieldValue(
                                  `customize_columns.${key}`,
                                  event.currentTarget.value
                                );
                              }}
                            />

                            <PermissionTooltip disabled={!canWrite()}>
                              <ActionButton
                                icon={IconTrash}
                                variant="white"
                                color="red.5"
                                onClick={() => {
                                  const updated = {
                                    ...form.values.customize_columns,
                                  };
                                  delete updated[key];
                                  form.setFieldValue(
                                    "customize_columns",
                                    updated
                                  );
                                }}
                                disabled={!canWrite()}
                              />
                            </PermissionTooltip>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </form>
      </div>
    </DetailPageLayout>
  );
};

export default TemplateDetailPage;
